import {Box, Grid, Stack, Typography, useTheme} from "@mui/material";
import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {clear, clearEntity, getEntity, PerformanceData} from "../Performance/PerformanceChartReducer";
import {useAppDispatch, useAppSelector} from "../../../Config/Hooks";
import {RootState} from "../../../Config/Store";
import useInterval from "../../../Shared/Hooks/useInterval";
import {
    ACard,
    ASectionTitle,
    AMetricCard,
    AMultiSelectFilter,
    AMultiSelectOption,
    AMultiSelectOptionId,
    Skeleton,
} from "@atiautomacao/ati-ui-library";
import {MenuItemStatus} from "../../../Shared/Components/Layout/menu/types/MenuItemStatus";
import {truncateNumber} from "../../../Utils/NumberUtil";
import DataNotFound from "../../../Shared/Components/DataNotFoundMessage";
import {formatAvailability, formatPerformanceRatio, getPerformanceRatioTone} from "../../../Utils/PerformanceRatioUtils";
import {pillColors} from "../Unavailability/Pill";
import {QueryClient, QueryClientProvider} from "react-query";
import PerformanceTotalizers from "../Performance/components/PerformanceTotalizers";
import {
    calculateProgress,
    formatValueToMega,
    hasExpectedData,
    NO_DATA,
    NO_DATA_PERCENTAGE,
} from "../Performance/utils/PerformanceDisplayUtils";
import {LocalDateTimeFormatISO} from "../../../Utils/DateFormatPatternUtils";
import {usePersistedSelection} from "../../../Shared/Hooks/usePersistedSelection";
import {POWER_STATION_IDS_FILTER_STORAGE_KEY} from "../../../Config/Constants";

export function sortByPerformanceRatio(list: Array<PerformanceData>): Array<PerformanceData> {
    return [...list].sort((a, b) => {
        const performanceRatioA = a.performanceRatio ?? 0;
        const performanceRatioB = b.performanceRatio ?? 0;

        if (performanceRatioA !== performanceRatioB) {
            return performanceRatioA - performanceRatioB;
        }
        return a.powerStationName.localeCompare(b.powerStationName, "pt-BR");
    });
}

const createPlaceholderCard = (ps: {id: number; name: string}): PerformanceData => ({
    id: ps.id,
    powerStationName: ps.name,
    etsStatus: "Ignored" as MenuItemStatus,
    activePower: 0,
    generationRate: 0,
    irradiance: 0,
    generatedEnergy: 0,
    expectedIrradiance: 0,
    expectedInjectedEnergy: 0,
    performanceRatio: 0,
    availability: 0,
    loading: true,
});

export default function DashboardOverviewPerformancePage() {
    const theme = useTheme();
    const queryClient = useMemo(() => new QueryClient({
        defaultOptions: {
            queries: {
                refetchOnWindowFocus: false,
            },
        },
    }), []);
    const entity: PerformanceData = useAppSelector((state: RootState) => state.performanceChart.entity);
    const dispatch = useAppDispatch();
    const {periodSynoptic} = useAppSelector((state: RootState) => state.performanceChart);
    const entitiesDashboard = useAppSelector(
        (state: RootState) => state.dashboardNavigationMenu.entities
    );
    const [actualIndex, setActualIndex] = useState(-1);
    const [powerStationList, setPowerStationList] = useState<Array<PerformanceData>>([]);
    const userConfig = useAppSelector((state: RootState) => state.userConfigurarion);
    const prevPeriodKeyRef = useRef<string>("");

    const powerStationOptions = useMemo<AMultiSelectOption[]>(
        () => [...(entitiesDashboard ?? [])]
            .sort((a, b) => a.name.localeCompare(b.name, "pt-BR"))
            .map((e) => ({id: e.id, label: e.name})),
        [entitiesDashboard]
    );

    const powerStationOptionIds = useMemo<AMultiSelectOptionId[]>(
        () => powerStationOptions.map((o) => o.id),
        [powerStationOptions]
    );

    const {
        selectedIds: selectedPowerStationIds,
        setSelectedIds: setSelectedPowerStationIds,
        isInitialized,
    } = usePersistedSelection<AMultiSelectOptionId>({
        storageKey: POWER_STATION_IDS_FILTER_STORAGE_KEY,
        allOptionIds: powerStationOptionIds,
    });

    const handleApplyPowerStationFilter = useCallback(
        (ids: AMultiSelectOptionId[]) => {
            if (ids.length === 0) return;
            setSelectedPowerStationIds(ids);
        },
        [setSelectedPowerStationIds]
    );

    const filteredEntities = useMemo(() => {
        if (!entitiesDashboard) return [];
        if (!isInitialized) return entitiesDashboard;
        const selectedSet = new Set(selectedPowerStationIds.map((id) => Number(id)));
        return entitiesDashboard.filter((e) => selectedSet.has(e.id));
    }, [isInitialized, selectedPowerStationIds, entitiesDashboard]);

    const filteredPowerStationIds = useMemo(
        () => filteredEntities.map((e) => e.id),
        [filteredEntities]
    );

    const filteredIdsKey = useMemo(
        () => filteredPowerStationIds.join(","),
        [filteredPowerStationIds]
    );

    const isPeriodValid = !!periodSynoptic && !periodSynoptic.fromDateTime.toString().includes("999Z");

    const periodKey = useMemo(() => {
        if (!periodSynoptic) return "";
        return `${periodSynoptic.fromDateTime}-${periodSynoptic.toDateTime}`;
    }, [periodSynoptic]);

    useEffect(() => {
        dispatch(clear());
        dispatch(clearEntity());

        if (!isPeriodValid || filteredEntities.length === 0) {
            setPowerStationList([]);
            setActualIndex(-1);
            prevPeriodKeyRef.current = periodKey;
            return;
        }

        const periodChanged = prevPeriodKeyRef.current !== periodKey;
        prevPeriodKeyRef.current = periodKey;

        setPowerStationList((prev) => {
            const prevById = new Map(prev.map((item) => [item.id, item]));
            return filteredEntities.map((ps) => {
                const existing = prevById.get(ps.id);
                // Filter-only change: keep already-loaded cards (no skeleton). New ids load.
                if (!periodChanged && existing && existing.loading === false) {
                    return existing;
                }
                if (!periodChanged && existing) {
                    return existing;
                }
                return createPlaceholderCard(ps);
            });
        });
        setActualIndex(0);
    }, [periodKey, filteredIdsKey, isPeriodValid, filteredEntities, dispatch]);

    const markItemAsError = (currentId: number) => (prevList: Array<PerformanceData>) =>
        prevList.map((item) => (item.id === currentId ? {...item, loading: false, error: true} : item));

    useEffect(() => {
        if (actualIndex <= -1 || filteredEntities.length === 0 || actualIndex >= filteredEntities.length) {
            return;
        }

        const currentId = filteredEntities[actualIndex].id;
        dispatch(getEntity({period: periodSynoptic, powerStationId: currentId})).then((result) => {
            if (result.meta.requestStatus === "rejected") {
                setPowerStationList(markItemAsError(currentId));
            }
            setActualIndex((idx) => idx + 1);
        });
        // Intentionally depend on actualIndex + filtered set; period is read from closure when index advances
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [actualIndex, filteredIdsKey]);

    useInterval(() => {
        if (filteredEntities.length === 0 || !isPeriodValid) return;
        setActualIndex(0);
    }, userConfig.mainUpdateInterval);

    useEffect(() => {
        if (entity) {
            setPowerStationList((prevList) =>
                prevList.map((item) => {
                    if (item.id === entity.id && item.powerStationName === entity.powerStationName) {
                        return {...entity, loading: false};
                    }
                    return item;
                })
            );
        }
    }, [entity, actualIndex]);

    const metricLabelSx = {
        fontFamily: theme.typography.fontFamily,
        fontWeight: 400,
        fontSize: "12px",
        lineHeight: "15px",
        letterSpacing: "0.2px",
    };

    /**
     * SGD-3680: apenas a ausência de dado esperado é exibida como "-" e "- %". Um valor
     * esperado igual a zero continua sendo exibido como 0, mas sem percentual, porque a
     * razão medido/esperado não é calculável.
     */
    const buildComparisonMetric = (
        label: string,
        measuredValue: number,
        expectedValue: number,
        color: string
    ) => {
        const progress = calculateProgress(measuredValue, expectedValue);

        return {
            label: {label, sx: metricLabelSx},
            value: `${formatValueToMega(measuredValue)} (${progress != null ? `${progress}%` : NO_DATA_PERCENTAGE})`,
            secondaryValue: hasExpectedData(expectedValue) ? formatValueToMega(expectedValue) : NO_DATA,
            // 0 e não undefined: o AMetricCard omite a barra quando progress é nulo,
            // o que alteraria a altura do card.
            progress: progress ?? 0,
            valueColor: color,
            barColor: color,
        };
    };

    const sortedPowerStationList = useMemo(() => {
        return sortByPerformanceRatio(powerStationList);
    }, [powerStationList]);

    return (
        <Box sx={{display: "flex", flexDirection: "column", alignItems: "center", paddingLeft: 2, paddingRight: 2}}>
            <ASectionTitle
                styleContainer={{padding: "32px 24px 24px 24px"}}
                titleLabel="Performance"
                contentChildren={
                    <Stack direction="row" sx={{flexShrink: 0, alignItems: "center"}}>
                        <AMultiSelectFilter
                            options={powerStationOptions}
                            selectedIds={selectedPowerStationIds}
                            onApply={handleApplyPowerStationFilter}
                            triggerLabel="Usinas"
                            searchPlaceholder="Buscar usina..."
                        />
                    </Stack>
                }
            />
            {isPeriodValid && filteredPowerStationIds.length > 0 && (
                <QueryClientProvider client={queryClient}>
                    <PerformanceTotalizers
                        powerStationIds={filteredPowerStationIds}
                        startDateTime={LocalDateTimeFormatISO(periodSynoptic.fromDateTime)}
                        endDateTime={LocalDateTimeFormatISO(periodSynoptic.toDateTime)}
                    />
                </QueryClientProvider>
            )}
            {sortedPowerStationList.length > 0 ? (
                <Grid container sx={{gap: "10px", paddingLeft: 3}}>
                    {sortedPowerStationList.map((data) => {
                        const performanceRatioTone = getPerformanceRatioTone(truncateNumber(data.performanceRatio, 1));

                        const performanceRatioColor = pillColors[performanceRatioTone].fg;
                        const performanceRatioBackground = pillColors[performanceRatioTone].bg;

                        if (data.loading) {
                            return (
                                <Grid item key={data.id}>
                                    <Box
                                        sx={{
                                            backgroundColor: "transparent",
                                            backgroundImage: "none",
                                            boxShadow: "none",
                                            border: "none",
                                            borderRadius: 0,
                                            padding: 0,
                                            width: "179.22px",
                                            height: "150px",
                                        }}
                                    >
                                        <Skeleton animation="wave" height={150} width="100%" variant="rectangular"/>
                                    </Box>
                                </Grid>
                            );
                        }
                        if (data.error) {
                            return (
                                <Grid item key={data.id}>
                                    <Box
                                        sx={{
                                            border: "0.5px solid",
                                            borderColor: "divider",
                                            display: "flex",
                                            flexDirection: "column",
                                            width: "179.22px",
                                            height: "234px",
                                            gap: "12px",
                                            opacity: 1,
                                            borderRadius: 2,
                                            bgcolor: "background.paper",
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "center",
                                                padding: "16px",
                                            }}
                                        >
                                            <Typography
                                                sx={{
                                                    fontFamily: "Work Sans",
                                                    fontWeight: 600,
                                                    fontSize: "14px",
                                                    lineHeight: "16px",
                                                }}
                                            >
                                                {data.powerStationName.length > 15
                                                    ? data.powerStationName.substring(0, 15) + "..."
                                                    : data.powerStationName}
                                            </Typography>
                                        </Box>

                                        <DataNotFound/>
                                    </Box>
                                </Grid>
                            );
                        }
                        return (
                            <Grid item key={data.id}>
                                <AMetricCard
                                    title={
                                        data.powerStationName.length > 15
                                            ? data.powerStationName.substring(0, 15) + "..."
                                            : data.powerStationName
                                    }
                                    metrics={[
                                        {
                                            label: {label: "PR"},
                                            value: `${formatPerformanceRatio(truncateNumber(data.performanceRatio, 2))}%`,
                                            variant: "chip",
                                            valueColor: performanceRatioColor,
                                            barColor: performanceRatioBackground,
                                            sx: {justifyContent: "center", alignItems: "center"},
                                        },
                                        buildComparisonMetric(
                                            "Energia gerada (MWh)",
                                            data.generatedEnergy,
                                            data.expectedInjectedEnergy,
                                            performanceRatioColor
                                        ),
                                        buildComparisonMetric(
                                            "Irradiação (kWh/m²)",
                                            data.irradiance,
                                            data.expectedIrradiance,
                                            performanceRatioColor
                                        ),
                                        {
                                            label: {
                                                label: "Disponibilidade",
                                                sx: {
                                                    fontFamily: theme.typography.fontFamily,
                                                    fontWeight: 400,
                                                    fontSize: "12px",
                                                    color: "text.secondary",
                                                },
                                            },
                                            variant: "inline",
                                            value: `${formatAvailability(data.availability)}%`,
                                            valueColor: theme.palette.text.secondary,
                                        },
                                    ]}
                                />
                            </Grid>
                        );
                    })}
                </Grid>
            ) : (
                <Grid container spacing={1}>
                    <ACard>Dados não encontrados</ACard>
                </Grid>
            )}
        </Box>
    );
}
